import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { ComissoesHierarquicaResponse } from '@/features/comissoes/types/comissao';

type Comissao = Database['public']['Tables']['comissoes']['Row'];
type ComissaoInsert = Database['public']['Tables']['comissoes']['Insert'];
type ComissaoUpdate = Database['public']['Tables']['comissoes']['Update'];
type StatusPagamento = 'PENDENTE' | 'PAGA' | 'CANCELADA';

export interface ComissaoFiltros {
  consultor_id?: string;
  status_pagamento?: StatusPagamento;
  data_inicio?: string;
  data_fim?: string;
  page?: number;
  per_page?: number;
}

export interface ComissoesResponse {
  data: Comissao[];
  total: number;
  page: number;
  per_page: number;
  last_page: number;
}

export interface ExportOptions {
  formato: 'excel' | 'pdf';
  filtros?: ComissaoFiltros;
}

export interface MonthlyReportData {
  mes: number;
  ano: number;
  total_comissoes: number;
  valor_total: number;
  comissoes_por_tipo: {
    relacionador: number;
    lider: number;
    supervisor: number;
  };
}

export class ComissaoSupabaseService {
  /**
   * Lista comissões com filtros e paginação
   */
  async listarComissoes(filtros?: ComissaoFiltros): Promise<ComissoesResponse> {
    try {
      // Query básica primeiro, depois buscar relacionamentos separadamente
      let query = supabase
        .from('comissoes')
        .select('*', { count: 'exact' });

      // Aplicar filtros
      if (filtros?.consultor_id) {
        query = query.eq('consultor_id', filtros.consultor_id);
      }
      if (filtros?.status_pagamento) {
        query = query.eq('status_pagamento', filtros.status_pagamento);
      }
      if (filtros?.data_inicio) {
        query = query.gte('created_at', filtros.data_inicio);
      }
      if (filtros?.data_fim) {
        query = query.lte('created_at', filtros.data_fim);
      }

      // Calcular range de paginação corretamente
      const page = filtros?.page || 1;
      const perPage = filtros?.per_page || 50;
      const from = (page - 1) * perPage;
      const to = from + perPage - 1;

      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      // Buscar relacionamentos separadamente para evitar problemas de join
      const comissoesComRelacionamentos = await Promise.all(
        (data || []).map(async (comissao: any) => {
          // Buscar contrato
          const { data: contrato } = await supabase
            .from('contratos')
            .select(`
              id,
              contrato_numero,
              valor,
              status,
              clientes(
                id,
                nome,
                cpf
              )
            `)
            .eq('id', comissao.contrato_id)
            .single();

          // Buscar consultor
          const { data: consultor } = await supabase
            .from('consultores')
            .select('id, nome, email, tipo')
            .eq('id', comissao.consultor_id)
            .single();

          return {
            ...comissao,
            contrato: contrato || null,
            consultor: consultor || null
          };
        })
      );

      return {
        data: comissoesComRelacionamentos,
        total: count || 0,
        page: page,
        per_page: perPage,
        last_page: Math.ceil((count || 0) / perPage)
      };
    } catch (error) {
      console.error('Erro ao listar comissões:', error);
      throw error;
    }
  }

  /**
   * Lista comissões de forma hierárquica (agrupadas por consultor)
   */
  async listarComissoesHierarquica(filtros?: ComissaoFiltros): Promise<ComissoesHierarquicaResponse> {
    try {
      // Primeiro, buscar todas as comissões
      const comissoesResponse = await this.listarComissoes({
        ...filtros,
        per_page: 1000 // Buscar todas para agrupar
      });

      // Agrupar por consultor
      const consultoresMap = new Map<number, any>();
      
      for (const comissao of comissoesResponse.data) {
        const comissaoData = comissao as any;
        const consultorId = comissaoData.consultor_id;
        
        if (!consultoresMap.has(consultorId)) {
          consultoresMap.set(consultorId, {
            consultor: comissaoData.consultor || { id: consultorId, nome: 'Consultor não encontrado', tipo: 'relacionador' },
            comissoes: [],
            totalPendente: 0,
            totalComissoes: 0
          });
        }
        
        const consultorData = consultoresMap.get(consultorId);
        consultorData.comissoes.push(comissao);
        consultorData.totalComissoes += comissaoData.valor_comissao || 0;
        
        if (comissaoData.status_pagamento === 'PENDENTE') {
          consultorData.totalPendente += comissaoData.valor_comissao || 0;
        }
      }

      const consultoresComissoes = Array.from(consultoresMap.values());

      return {
        data: consultoresComissoes,
        total: consultoresComissoes.length,
        totalGeral: comissoesResponse.total,
        page: filtros?.page || 1,
        per_page: filtros?.per_page || 50
      };
    } catch (error) {
      console.error('Erro ao listar comissões hierárquicas:', error);
      throw error;
    }
  }

  /**
   * Busca uma comissão específica por ID
   */
  async buscarComissao(id: string): Promise<Comissao | null> {
    try {
      const { data, error } = await supabase
        .from('comissoes')
        .select(`
          *,
          contratos!inner(
            *,
            clientes!inner(*),
            consultores!inner(*)
          ),
          consultores!inner(*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') return null;
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Erro ao buscar comissão:', error);
      throw error;
    }
  }

  /**
   * Cria uma nova comissão
   */
  async criarComissao(data: ComissaoInsert): Promise<Comissao> {
    try {
      const { data: result, error } = await supabase
        .from('comissoes')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    } catch (error) {
      console.error('Erro ao criar comissão:', error);
      throw error;
    }
  }

  /**
   * Atualiza o status de pagamento de uma comissão
   */
  async atualizarStatusPagamento(
    id: string, 
    status_pagamento: StatusPagamento, 
    data_pagamento?: string
  ): Promise<Comissao> {
    try {
      const updateData: any = { status_pagamento };
      
      if (data_pagamento) {
        updateData.data_pagamento = new Date(data_pagamento).toISOString();
      }

      const { data, error } = await supabase
        .from('comissoes')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Erro ao atualizar status de pagamento:', error);
      throw error;
    }
  }

  /**
   * Atualiza uma comissão
   */
  async atualizarComissao(id: string, data: ComissaoUpdate): Promise<Comissao> {
    try {
      const { data: result, error } = await supabase
        .from('comissoes')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    } catch (error) {
      console.error('Erro ao atualizar comissão:', error);
      throw error;
    }
  }

  /**
   * Exclui uma comissão
   */
  async excluirComissao(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('comissoes')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Erro ao excluir comissão:', error);
      throw error;
    }
  }

  /**
   * Exclui múltiplas comissões
   */
  async excluirComissoesEmLote(ids: string[]): Promise<{ message: string; deletedCount: number }> {
    try {
      const { error, count } = await supabase
        .from('comissoes')
        .delete({ count: 'exact' })
        .in('id', ids);

      if (error) throw error;

      return {
        message: `${count || 0} comissões excluídas com sucesso`,
        deletedCount: count || 0
      };
    } catch (error) {
      console.error('Erro ao excluir comissões em lote:', error);
      throw error;
    }
  }

  /**
   * Gera comissões automaticamente para um contrato
   */
  async gerarComissoesContrato(contrato_id: string): Promise<Comissao[]> {
    try {
      // Por enquanto, vamos implementar a lógica diretamente
      // Buscar dados do contrato
      const { data: contrato, error: contratoError } = await supabase
        .from('contratos')
        .select('*')
        .eq('id', contrato_id)
        .single();

      if (contratoError || !contrato) {
        throw new Error('Contrato não encontrado');
      }

      // Buscar dados do consultor
      const { data: consultor, error: consultorError } = await supabase
        .from('consultores')
        .select('*')
        .eq('id', (contrato as any).consultor_id)
        .single();

      if (consultorError || !consultor) {
        throw new Error('Consultor não encontrado');
      }

      // Verificar se já existem comissões para este contrato
      const { data: comissoesExistentes, error: existentesError } = await supabase
        .from('comissoes')
        .select('id')
        .eq('contrato_id', contrato_id);

      if (existentesError) throw existentesError;

      if (comissoesExistentes && comissoesExistentes.length > 0) {
        throw new Error('Comissões já foram geradas para este contrato');
      }

      const comissoesParaCriar: any[] = [];

      // Comissão do relacionador (0.3%)
      comissoesParaCriar.push({
        contrato_id,
        consultor_id: (contrato as any).consultor_id,
        tipo: 'RELACIONADOR',
        valor: ((contrato as any).valor_credito || 0) * 0.003,
        percentual: 0.3,
        tenant_id: (contrato as any).tenant_id
      });

      // Se houver líder, criar comissão (0.4%)
      if ((consultor as any).lider_id) {
        comissoesParaCriar.push({
          contrato_id,
          consultor_id: (consultor as any).lider_id,
          tipo: 'LIDER',
          valor: ((contrato as any).valor_credito || 0) * 0.004,
          percentual: 0.4,
          tenant_id: (contrato as any).tenant_id
        });
      }

      // Se houver supervisor, criar comissão (0.2%)
      if ((consultor as any).supervisor_id) {
        comissoesParaCriar.push({
          contrato_id,
          consultor_id: (consultor as any).supervisor_id,
          tipo: 'SUPERVISOR',
          valor: ((contrato as any).valor_credito || 0) * 0.002,
          percentual: 0.2,
          tenant_id: (contrato as any).tenant_id
        });
      }

      // Inserir comissões
      const { data: comissoesCriadas, error: criarError } = await supabase
        .from('comissoes')
        .insert(comissoesParaCriar)
        .select(`
          *,
          contratos!inner(*),
          consultores!inner(*)
        `);

      if (criarError) throw criarError;

      return comissoesCriadas || [];
    } catch (error) {
      console.error('Erro ao gerar comissões do contrato:', error);
      throw error;
    }
  }

  /**
   * Exporta comissões para Excel ou PDF
   */
  async exportarComissoes(options: ExportOptions): Promise<Blob> {
    try {
      // Por enquanto, vamos implementar uma exportação simples
      // Em uma implementação completa, isso seria feito via Edge Function
      await this.listarComissoes(options.filtros);
      
      if (options.formato === 'excel') {
        // Implementar geração de Excel usando uma biblioteca como xlsx
        // Por enquanto, retornamos um blob vazio
        return new Blob(['Exportação Excel em desenvolvimento'], { 
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
        });
      } else {
        // Implementar geração de PDF usando uma biblioteca como jsPDF
        // Por enquanto, retornamos um blob vazio
        return new Blob(['Exportação PDF em desenvolvimento'], { 
          type: 'application/pdf' 
        });
      }
    } catch (error) {
      console.error('Erro ao exportar comissões:', error);
      throw error;
    }
  }

  /**
   * Gera relatório mensal de comissões
   */
  async gerarRelatorioMensal(month: number, year: number): Promise<MonthlyReportData> {
    try {
      const dataInicio = `${year}-${month.toString().padStart(2, '0')}-01`;
      const dataFim = `${year}-${month.toString().padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;

      const { data, error } = await supabase
        .from('comissoes')
        .select('tipo, valor, created_at')
        .gte('created_at', dataInicio)
        .lte('created_at', dataFim)
        .is('deleted_at', null);

      if (error) throw error;

      const totalComissoes = data?.length || 0;
      const valorTotal = data?.reduce((sum, comissao) => sum + ((comissao as any).valor || 0), 0) || 0;

      const comissoesPorTipo = {
        relacionador: data?.filter(c => (c as any).tipo === 'RELACIONADOR').length || 0,
        lider: data?.filter(c => (c as any).tipo === 'LIDER').length || 0,
        supervisor: data?.filter(c => (c as any).tipo === 'SUPERVISOR').length || 0,
      };

      return {
        mes: month,
        ano: year,
        total_comissoes: totalComissoes,
        valor_total: valorTotal,
        comissoes_por_tipo: comissoesPorTipo
      };
    } catch (error) {
      console.error('Erro ao gerar relatório mensal:', error);
      throw error;
    }
  }

  /**
   * Busca comissões por consultor
   */
  async buscarComissoesPorConsultor(consultor_id: string): Promise<Comissao[]> {
    try {
      const { data, error } = await supabase
        .from('comissoes')
        .select(`
          *,
          contratos!inner(
            *,
            clientes!inner(*)
          ),
          consultores!inner(*)
        `)
        .eq('consultor_id', consultor_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar comissões por consultor:', error);
      throw error;
    }
  }

  /**
   * Busca comissões pendentes
   */
  async buscarComissoesPendentes(): Promise<Comissao[]> {
    try {
      const { data, error } = await supabase
        .from('comissoes')
        .select(`
          *,
          contratos!inner(
            *,
            clientes!inner(*)
          ),
          consultores!inner(*)
        `)
        .eq('status_pagamento', 'PENDENTE')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar comissões pendentes:', error);
      throw error;
    }
  }
}

// Instância singleton do serviço
export const comissaoSupabaseService = new ComissaoSupabaseService();
